import { create } from 'zustand';

export interface OrgArea {
  id: string;
  name: string;
  slug: string;
  type: string;
  visibility: string;
  isDefault: boolean;
  isDm: boolean;
  icon: string | null;
  iconColor: string | null;
  description: string | null;
  isArchived: boolean;
  isMember: boolean;
  memberCount: number;
  lastMessageAt: string | null;
  sortOrder: number | null;
}

export interface OrgMember {
  userId: string;
  fullName: string;
  displayName: string;
  profilePictureUrl: string | null;
  homeCity?: string | null;
  role: string;
  joinedAt?: string;
  orgEmail?: string;
}

export interface DmPreview {
  id: string;
  name: string;
  participants: {
    userId: string;
    fullName: string;
    displayName: string;
    profilePictureUrl: string | null;
  }[];
  lastMessage: {
    content: string | null;
    authorId: string;
    createdAt: string;
    isDeleted: boolean;
  } | null;
}

interface OrgState {
  selectedOrgId: string | null;
  areas: OrgArea[];
  members: OrgMember[];
  dms: DmPreview[];

  // Slug lookup maps
  orgSlugById: Record<string, string>;
  orgIdBySlug: Record<string, string>;
  areaSlugById: Record<string, string>;
  areaIdBySlug: Record<string, string>;

  // Per-area notification level (fetched lazily from area chat header)
  areaNotificationLevels: Record<string, 'all' | 'mentions' | 'muted'>;

  setSelectedOrg: (orgId: string) => void;
  setAreas: (areas: OrgArea[]) => void;
  setMembers: (members: OrgMember[]) => void;
  setDms: (dms: DmPreview[]) => void;
  /** Live DM sidebar updates: refresh a DM's last-message preview (and float it to the top on new). */
  updateDmLastMessage: (
    areaId: string,
    patch: { content?: string | null; authorId?: string; createdAt?: string; isDeleted?: boolean },
    resort?: boolean,
  ) => void;
  setOrgSlugs: (orgs: { id: string; slug: string }[]) => void;
  updateArea: (areaId: string, updates: Partial<OrgArea>) => void;
  setAreaNotificationLevel: (areaId: string, level: 'all' | 'mentions' | 'muted') => void;
  reset: () => void;
}

export const useOrgStore = create<OrgState>((set) => ({
  selectedOrgId: null,
  areas: [],
  members: [],
  dms: [],
  orgSlugById: {},
  orgIdBySlug: {},
  areaSlugById: {},
  areaIdBySlug: {},
  areaNotificationLevels: {},

  setSelectedOrg: (orgId) => set({ selectedOrgId: orgId }),

  setAreas: (areas) => {
    const areaSlugById: Record<string, string> = {};
    const areaIdBySlug: Record<string, string> = {};
    for (const a of areas) {
      if (a.slug) {
        areaSlugById[a.id] = a.slug;
        areaIdBySlug[a.slug] = a.id;
      }
    }
    set({ areas, areaSlugById, areaIdBySlug });
  },

  setMembers: (members) => set({ members }),
  setDms: (dms) => set({ dms }),

  updateDmLastMessage: (areaId, patch, resort = false) =>
    set((s) => {
      if (!s.dms.some((d) => d.id === areaId)) return s;
      const dms = s.dms.map((d) =>
        d.id === areaId
          ? {
              ...d,
              lastMessage: {
                content: patch.content ?? d.lastMessage?.content ?? null,
                authorId: patch.authorId ?? d.lastMessage?.authorId ?? '',
                createdAt: patch.createdAt ?? d.lastMessage?.createdAt ?? new Date().toISOString(),
                isDeleted: patch.isDeleted ?? d.lastMessage?.isDeleted ?? false,
              },
            }
          : d,
      );
      if (resort) {
        dms.sort((a, b) => {
          if (!a.lastMessage && !b.lastMessage) return 0;
          if (!a.lastMessage) return 1;
          if (!b.lastMessage) return -1;
          return new Date(b.lastMessage.createdAt).getTime() - new Date(a.lastMessage.createdAt).getTime();
        });
      }
      return { dms };
    }),

  updateArea: (areaId, updates) =>
    set((state) => ({
      areas: state.areas.map((a) => (a.id === areaId ? { ...a, ...updates } : a)),
    })),

  setOrgSlugs: (orgs) => {
    const orgSlugById: Record<string, string> = {};
    const orgIdBySlug: Record<string, string> = {};
    for (const o of orgs) {
      orgSlugById[o.id] = o.slug;
      orgIdBySlug[o.slug] = o.id;
    }
    set({ orgSlugById, orgIdBySlug });
  },

  setAreaNotificationLevel: (areaId, level) =>
    set((state) => ({
      areaNotificationLevels: { ...state.areaNotificationLevels, [areaId]: level },
    })),

  reset: () => set({
    selectedOrgId: null,
    areas: [],
    members: [],
    dms: [],
    orgSlugById: {},
    orgIdBySlug: {},
    areaSlugById: {},
    areaIdBySlug: {},
    areaNotificationLevels: {},
  }),
}));
